import styled from '@emotion/styled';
import { Controller } from 'react-hook-form';
import { FormSelect } from 'ui/components/elements/FormSelect/FormSelect';
import { FormInput } from 'ui/components/elements/FormInput/FormInput';
import { FormError } from 'ui/components/elements/FormError/FormError';
import { FormLabel } from 'ui/components/elements/FormLabel/FormLabel';
import { FormTextArea } from 'ui/components/elements/FormTextArea/FormTextArea';
import { ActionButton } from 'ui/components/elements/ActionButton/ActionButton';
import { FormAreaSelect } from 'ui/components/elements/FormAreaSelect/FormAreaSelect';
import { FormMultiCheckBox } from 'ui/components/elements/FormMultiCheckBox/FormMultiCheckBox';
import { FormCheckbox } from 'ui/components/elements/FormCheckbox/FormCheckbox';
import { useModal } from 'ui/components/screens/modals/useModal';
import { ImageUpload } from 'ui/components/features/ImageUpload/ImageUpload';
import { CompanyNameSearch } from 'ui/components/screens/modals/CompanyNameSearch/CompanyNameSearch';
import { TemplateModal } from 'ui/components/screens/modals/TemplateModal/TemplateModal';
import { CompanyInfoVisibilityNotice } from 'ui/components/features/CompanyInfoVisibilityNotice/CompanyInfoVisibilityNotice';
import { Preview } from 'ui/components/buyer/patterns/BuyerEdit/Preview';
import { isNotRegistrationComplete } from 'domain/entities/BuyerUser/BuyerUser';
import { SubTitleLabel } from 'ui/components/elements/ColorLabel/SubTitleLabel';
import { getWindowText } from 'utils/form';
import IconSearch from 'ui/images/form/search.svg';
import MailIcon from 'ui/images/icons/mail.svg';
import { mqUser } from 'utils/responsive';

const Container = styled.div`
    display: flex;
    gap: 5px;
    width: 100%;
    margin: 0 auto;

    ${mqUser('SP')} {
        flex-direction: column;
    }
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 600px;
    margin: 0 auto 20px auto;
    background-color: #ffffff;
    border-radius: 8px;
    padding: 30px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const ButtonContainer = styled.div`
    margin: 20px auto 0;
`;

const SearchButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--dark-blue);
    color: var(--white);
    font-size: var(--font-medium);
    font-weight: 700;
    border-radius: 30px;
    cursor: pointer;
    padding: 15px 25px;
    width: fit-content;

    img {
        margin-left: 8px;
        width: 20px;
        height: 20px;
    }
`;

const Description = styled.div`
    font-size: 14px;
    font-weight: var(--font-bold);
    color: #626262;
    text-align: left;
`;

const FlexContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 0 10px;
    width: 100%;
    margin: 0 auto;
`;

const TemplateWrapper = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--lightblue);
    border: 2px solid var(--main-color-blue);
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 900;
    gap: 3px;
    padding: 4px 8px;
    color: var(--main-color-blue);
    transition: opacity 0.2s ease-out;
    width: fit-content;
    &:hover {
        opacity: 0.8;
    }
    img {
        width: 15px;
        pointer-events: none;
    }
`;

const Section = styled.div`
    padding: 30px 0;
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const RangeSelectsContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const TildeText = styled.span`
    font-size: var(--font-medium);
    color: var(--dark-blue);
    font-weight: 600;
    margin: 0 5px;
`;

const RangeSelectWrapper = styled.div`
    flex: 1;
`;

const RangeContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 0 10px;
    width: 100%;
    margin: 0 auto;
`;

const RangeLabel = styled.div`
    font-size: var(--font-medium);
    font-weight: 600;
    color: var(--dark-blue);
    margin-bottom: 5px;
`;

interface BuyerEditFormProps {
    user: any;
    control: any;
    loading: boolean;
    errors: any;
    areaChoices: any[];
    employeeChoices: any[];
    revenueChoices: any[];
    industryChoices: any[];
    employeeLowerChoices: any[];
    employeeUpperChoices: any[];
    revenueScaleLowerChoices: any[];
    revenueScaleUpperChoices: any[];
    profitLevelLowerChoices: any[];
    profitLevelUpperChoices: any[];
    netAssetsLevelLowerChoices: any[];
    netAssetsLevelUpperChoices: any[];
    purchaseAmountLowerChoices: any[];
    purchaseAmountUpperChoices: any[];
    hasAcquisitionExperienceChoices: any[];
    showSnackbar: boolean;
    editing?: boolean;
    setShowSnackbar: (value: boolean) => void;
    submit: () => void;
    setValue: (name: any, value: any) => void;
    watch: (name?: string) => any;
    onCompanySelect: (company: any) => void;
    setEditing?: (editing: boolean) => void;
    unPublish?: () => void;
    reOpen?: () => void;
}

export const BuyerEditForm: React.FC<BuyerEditFormProps> = ({
    user,
    control,
    loading,
    errors,
    areaChoices,
    employeeChoices,
    revenueChoices,
    industryChoices,
    employeeLowerChoices,
    employeeUpperChoices,
    revenueScaleLowerChoices,
    revenueScaleUpperChoices,
    profitLevelLowerChoices,
    profitLevelUpperChoices,
    netAssetsLevelLowerChoices,
    netAssetsLevelUpperChoices,
    purchaseAmountLowerChoices,
    purchaseAmountUpperChoices,
    hasAcquisitionExperienceChoices,
    editing = true,
    submit,
    setValue,
    watch,
    onCompanySelect,
    setEditing,
    unPublish,
    reOpen,
}) => {
    const companySearchModal = useModal();
    const templateModal = useModal();

    if (!user) {
        return null;
    }

    const notRegistrationComplete = isNotRegistrationComplete(user) || !user.buyer.name;

    const handleTemplateClick = () => {
        templateModal.openModal();
    };

    const handleCompanySearchClick = () => {
        companySearchModal.openModal();
    };

    const handleSelectTemplate = (text: string) => {
        const currentQuestion = watch('question') || '';
        if (currentQuestion.trim()) {
            setValue('question', currentQuestion + '\n' + text);
        } else {
            setValue('question', text);
        }
    };

    const companyLabel = watch('is_sole_proprietor') ? '貴事業' : '貴社';

    return (
        <>
            {companySearchModal.createModal(
                <CompanyNameSearch
                    onSelect={(company) => onCompanySelect(company)}
                    closeModal={companySearchModal.closeModal}
                />,
                { scrollable: false },
            )}
            {templateModal.createModal(
                <TemplateModal
                    onSelect={handleSelectTemplate}
                    onClose={templateModal.closeModal}
                    companyType="buyer"
                />,
                {
                    keyboard: true,
                    maskClosable: true,
                    onClose: templateModal.closeModal,
                },
            )}

            <Container>
                {editing && (
                    <Wrapper>
                        <Section>
                            <SubTitleLabel>買収希望案件条件について教えてください</SubTitleLabel>
                            <RangeContainer>
                                <RangeLabel>買収希望金額</RangeLabel>
                                <RangeSelectsContainer>
                                    {purchaseAmountLowerChoices && purchaseAmountLowerChoices.length > 0 && (
                                        <RangeSelectWrapper>
                                            <Controller
                                                name="target_company_purchase_amount_lower"
                                                control={control}
                                                render={({ field }) => (
                                                    <div>
                                                        <FormSelect
                                                            {...field}
                                                            value={field.value ?? ''}
                                                            options={purchaseAmountLowerChoices.map(
                                                                ([value, label]) => ({
                                                                    value: value,
                                                                    label: label,
                                                                }),
                                                            )}
                                                            placeholder="下限を選択してください"
                                                        />
                                                        {errors.target_company_purchase_amount_lower && (
                                                            <FormError
                                                                message={
                                                                    errors.target_company_purchase_amount_lower.message
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </RangeSelectWrapper>
                                    )}
                                    <TildeText>〜</TildeText>
                                    {purchaseAmountUpperChoices && purchaseAmountUpperChoices.length > 0 && (
                                        <RangeSelectWrapper>
                                            <Controller
                                                name="target_company_purchase_amount_upper"
                                                control={control}
                                                render={({ field }) => (
                                                    <div>
                                                        <FormSelect
                                                            {...field}
                                                            value={field.value ?? ''}
                                                            options={purchaseAmountUpperChoices.map(
                                                                ([value, label]) => ({
                                                                    value: value,
                                                                    label: label,
                                                                }),
                                                            )}
                                                            placeholder="上限を選択してください"
                                                        />
                                                        {errors.target_company_purchase_amount_upper && (
                                                            <FormError
                                                                message={
                                                                    errors.target_company_purchase_amount_upper.message
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </RangeSelectWrapper>
                                    )}
                                </RangeSelectsContainer>
                            </RangeContainer>
                            <RangeContainer>
                                <RangeLabel>買収希望案件企業の従業員数</RangeLabel>
                                <RangeSelectsContainer>
                                    {employeeLowerChoices && employeeLowerChoices.length > 0 && (
                                        <RangeSelectWrapper>
                                            <Controller
                                                name="target_company_employee_size_lower"
                                                control={control}
                                                render={({ field }) => (
                                                    <div>
                                                        <FormSelect
                                                            {...field}
                                                            value={field.value ?? ''}
                                                            options={employeeLowerChoices.map(([value, label]) => ({
                                                                value: value,
                                                                label: label,
                                                            }))}
                                                            placeholder="下限を選択してください"
                                                        />
                                                        {errors.target_company_employee_size_lower && (
                                                            <FormError
                                                                message={
                                                                    errors.target_company_employee_size_lower.message
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </RangeSelectWrapper>
                                    )}
                                    <TildeText>〜</TildeText>
                                    {employeeUpperChoices && employeeUpperChoices.length > 0 && (
                                        <RangeSelectWrapper>
                                            <Controller
                                                name="target_company_employee_size_upper"
                                                control={control}
                                                render={({ field }) => (
                                                    <div>
                                                        <FormSelect
                                                            {...field}
                                                            value={field.value ?? ''}
                                                            options={employeeUpperChoices.map(([value, label]) => ({
                                                                value: value,
                                                                label: label,
                                                            }))}
                                                            placeholder="上限を選択してください"
                                                        />
                                                        {errors.target_company_employee_size_upper && (
                                                            <FormError
                                                                message={
                                                                    errors.target_company_employee_size_upper.message
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </RangeSelectWrapper>
                                    )}
                                </RangeSelectsContainer>
                            </RangeContainer>
                            <RangeContainer>
                                <RangeLabel>買収希望案件の売上高</RangeLabel>
                                <RangeSelectsContainer>
                                    {revenueScaleLowerChoices && revenueScaleLowerChoices.length > 0 && (
                                        <RangeSelectWrapper>
                                            <Controller
                                                name="target_company_revenue_scale_lower"
                                                control={control}
                                                render={({ field }) => (
                                                    <div>
                                                        <FormSelect
                                                            {...field}
                                                            value={field.value ?? ''}
                                                            options={revenueScaleLowerChoices.map(([value, label]) => ({
                                                                value: value,
                                                                label: label,
                                                            }))}
                                                            placeholder="下限を選択してください"
                                                        />
                                                        {errors.target_company_revenue_scale_lower && (
                                                            <FormError
                                                                message={
                                                                    errors.target_company_revenue_scale_lower.message
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </RangeSelectWrapper>
                                    )}
                                    <TildeText>〜</TildeText>
                                    {revenueScaleUpperChoices && revenueScaleUpperChoices.length > 0 && (
                                        <RangeSelectWrapper>
                                            <Controller
                                                name="target_company_revenue_scale_upper"
                                                control={control}
                                                render={({ field }) => (
                                                    <div>
                                                        <FormSelect
                                                            {...field}
                                                            value={field.value ?? ''}
                                                            options={revenueScaleUpperChoices.map(([value, label]) => ({
                                                                value: value,
                                                                label: label,
                                                            }))}
                                                            placeholder="上限を選択してください"
                                                        />
                                                        {errors.target_company_revenue_scale_upper && (
                                                            <FormError
                                                                message={
                                                                    errors.target_company_revenue_scale_upper.message
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </RangeSelectWrapper>
                                    )}
                                </RangeSelectsContainer>
                            </RangeContainer>
                            <RangeContainer>
                                <RangeLabel>買収希望案件の営業利益</RangeLabel>
                                <RangeSelectsContainer>
                                    {profitLevelLowerChoices && profitLevelLowerChoices.length > 0 && (
                                        <RangeSelectWrapper>
                                            <Controller
                                                name="target_company_profit_level_lower"
                                                control={control}
                                                render={({ field }) => (
                                                    <div>
                                                        <FormSelect
                                                            {...field}
                                                            value={field.value ?? ''}
                                                            options={profitLevelLowerChoices.map(([value, label]) => ({
                                                                value: value,
                                                                label: label,
                                                            }))}
                                                            placeholder="下限を選択してください"
                                                        />
                                                        {errors.target_company_profit_level_lower && (
                                                            <FormError
                                                                message={
                                                                    errors.target_company_profit_level_lower.message
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </RangeSelectWrapper>
                                    )}
                                    <TildeText>〜</TildeText>
                                    {profitLevelUpperChoices && profitLevelUpperChoices.length > 0 && (
                                        <RangeSelectWrapper>
                                            <Controller
                                                name="target_company_profit_level_upper"
                                                control={control}
                                                render={({ field }) => (
                                                    <div>
                                                        <FormSelect
                                                            {...field}
                                                            value={field.value ?? ''}
                                                            options={profitLevelUpperChoices.map(([value, label]) => ({
                                                                value: value,
                                                                label: label,
                                                            }))}
                                                            placeholder="上限を選択してください"
                                                        />
                                                        {errors.target_company_profit_level_upper && (
                                                            <FormError
                                                                message={
                                                                    errors.target_company_profit_level_upper.message
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </RangeSelectWrapper>
                                    )}
                                </RangeSelectsContainer>
                            </RangeContainer>
                            <RangeContainer>
                                <RangeLabel>買収希望案件企業の純資産金額</RangeLabel>
                                <RangeSelectsContainer>
                                    {netAssetsLevelLowerChoices && netAssetsLevelLowerChoices.length > 0 && (
                                        <RangeSelectWrapper>
                                            <Controller
                                                name="target_company_net_assets_level_lower"
                                                control={control}
                                                render={({ field }) => (
                                                    <div>
                                                        <FormSelect
                                                            {...field}
                                                            value={field.value ?? ''}
                                                            options={netAssetsLevelLowerChoices.map(
                                                                ([value, label]) => ({
                                                                    value: value,
                                                                    label: label,
                                                                }),
                                                            )}
                                                            placeholder="下限を選択してください"
                                                        />
                                                        {errors.target_company_net_assets_level_lower && (
                                                            <FormError
                                                                message={
                                                                    errors.target_company_net_assets_level_lower.message
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </RangeSelectWrapper>
                                    )}
                                    <TildeText>〜</TildeText>
                                    {netAssetsLevelUpperChoices && netAssetsLevelUpperChoices.length > 0 && (
                                        <RangeSelectWrapper>
                                            <Controller
                                                name="target_company_net_assets_level_upper"
                                                control={control}
                                                render={({ field }) => (
                                                    <div>
                                                        <FormSelect
                                                            {...field}
                                                            value={field.value ?? ''}
                                                            options={netAssetsLevelUpperChoices.map(
                                                                ([value, label]) => ({
                                                                    value: value,
                                                                    label: label,
                                                                }),
                                                            )}
                                                            placeholder="上限を選択してください"
                                                        />
                                                        {errors.target_company_net_assets_level_upper && (
                                                            <FormError
                                                                message={
                                                                    errors.target_company_net_assets_level_upper.message
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </RangeSelectWrapper>
                                    )}
                                </RangeSelectsContainer>
                            </RangeContainer>
                            <RangeContainer>
                                <RangeLabel>過去の買収実績の有無</RangeLabel>
                                <RangeSelectsContainer>
                                    {hasAcquisitionExperienceChoices && hasAcquisitionExperienceChoices.length > 0 && (
                                        <RangeSelectWrapper>
                                            <Controller
                                                name="has_acquisition_experience"
                                                control={control}
                                                render={({ field }) => (
                                                    <div>
                                                        <FormSelect
                                                            {...field}
                                                            value={field.value ?? ''}
                                                            options={hasAcquisitionExperienceChoices.map(
                                                                ([value, label]) => ({
                                                                    value: value,
                                                                    label: label,
                                                                }),
                                                            )}
                                                            placeholder="選択してください"
                                                        />
                                                        {errors.has_acquisition_experience && (
                                                            <FormError
                                                                message={errors.has_acquisition_experience.message}
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </RangeSelectWrapper>
                                    )}
                                </RangeSelectsContainer>
                            </RangeContainer>
                            <FlexContainer>
                                <FormLabel title="買収希望案件業種" />
                                {industryChoices && industryChoices.length > 0 && (
                                    <Controller
                                        name="target_company_industries"
                                        control={control}
                                        render={({ field }) => (
                                            <div>
                                                <FormMultiCheckBox
                                                    {...field}
                                                    options={industryChoices}
                                                    idPrefix="industry"
                                                />
                                                {errors.target_company_industries && (
                                                    <FormError message={errors.target_company_industries.message} />
                                                )}
                                            </div>
                                        )}
                                    />
                                )}
                            </FlexContainer>
                            <FlexContainer>
                                <FormLabel title="買収希望案件の業種詳細" />
                                <Controller
                                    name="target_company_industry_details"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormTextArea
                                                {...field}
                                                value={field.value ?? ''}
                                                rows={4}
                                                resize="vertical"
                                            />
                                            {errors.target_company_industry_details && (
                                                <FormError message={errors.target_company_industry_details.message} />
                                            )}
                                        </div>
                                    )}
                                />
                            </FlexContainer>
                            <FlexContainer>
                                <FormLabel title="買収希望案件エリア" />
                                {areaChoices && areaChoices.length > 0 && (
                                    <Controller
                                        name="target_area"
                                        control={control}
                                        render={({ field }) => (
                                            <div>
                                                <FormAreaSelect {...field} options={areaChoices} />
                                                {errors.target_area && (
                                                    <FormError message={errors.target_area.message} />
                                                )}
                                            </div>
                                        )}
                                    />
                                )}
                            </FlexContainer>
                            <FlexContainer>
                                <FormLabel title="その他希望事項等" />
                                <Controller
                                    name="other_buying_points"
                                    control={control}
                                    render={({ field }) => (
                                        <FormTextArea
                                            {...field}
                                            value={field.value ?? ''}
                                            placeholder="その他の希望事項があれば記載してください"
                                            resize="vertical"
                                        />
                                    )}
                                />
                            </FlexContainer>
                        </Section>
                        <CompanyInfoVisibilityNotice />
                        <SubTitleLabel>仲介会社に聞いてみたいこと</SubTitleLabel>
                        <Section>
                            <Description>
                                以下の項目について、具体的に知りたい内容があればチェックして詳細を入力してください
                            </Description>

                            <FlexContainer>
                                <Controller
                                    name="fee_structure"
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            id="fee_structure"
                                            disableBorder
                                            checked={field.value}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                        >
                                            M&Aに関する手数料水準等について
                                        </FormCheckbox>
                                    )}
                                />
                                {watch('fee_structure') && (
                                    <Controller
                                        name="fee_structure_txt"
                                        control={control}
                                        render={({ field }) => (
                                            <div>
                                                <FormTextArea
                                                    {...field}
                                                    value={field.value ?? ''}
                                                    hasError={!!errors.fee_structure_txt?.message}
                                                    placeholder="「M&Aに関する手数料水準等について」の詳細を入力"
                                                    resize="vertical"
                                                />
                                                {errors.fee_structure_txt?.message && (
                                                    <FormError message={errors.fee_structure_txt.message} />
                                                )}
                                            </div>
                                        )}
                                    />
                                )}

                                <Controller
                                    name="other_buyers"
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            id="other_buyers"
                                            disableBorder
                                            checked={field.value}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                        >
                                            他の買い手候補について
                                        </FormCheckbox>
                                    )}
                                />
                                {watch('other_buyers') && (
                                    <Controller
                                        name="other_buyers_txt"
                                        control={control}
                                        render={({ field }) => (
                                            <div>
                                                <FormTextArea
                                                    {...field}
                                                    value={field.value ?? ''}
                                                    hasError={!!errors.other_buyers_txt?.message}
                                                    placeholder="「他の買い手候補について」の詳細を入力"
                                                    resize="vertical"
                                                />
                                                {errors.other_buyers_txt?.message && (
                                                    <FormError message={errors.other_buyers_txt.message} />
                                                )}
                                            </div>
                                        )}
                                    />
                                )}

                                <Controller
                                    name="contract_status"
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            id="contract_status"
                                            disableBorder
                                            checked={field.value}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                        >
                                            提案案件の契約状況について
                                        </FormCheckbox>
                                    )}
                                />
                                {watch('contract_status') && (
                                    <Controller
                                        name="contract_status_txt"
                                        control={control}
                                        render={({ field }) => (
                                            <div>
                                                <FormTextArea
                                                    {...field}
                                                    value={field.value ?? ''}
                                                    hasError={!!errors.contract_status_txt?.message}
                                                    placeholder="「提案案件の契約状況について」の詳細を入力"
                                                    resize="vertical"
                                                />
                                                {errors.contract_status_txt?.message && (
                                                    <FormError message={errors.contract_status_txt.message} />
                                                )}
                                            </div>
                                        )}
                                    />
                                )}

                                <Controller
                                    name="process_flow"
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            id="process_flow"
                                            disableBorder
                                            checked={field.value}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                        >
                                            M&Aの具体的な流れについて
                                        </FormCheckbox>
                                    )}
                                />
                                {watch('process_flow') && (
                                    <Controller
                                        name="process_flow_txt"
                                        control={control}
                                        render={({ field }) => (
                                            <div>
                                                <FormTextArea
                                                    {...field}
                                                    value={field.value ?? ''}
                                                    hasError={!!errors.process_flow_txt?.message}
                                                    placeholder="「M&Aの具体的な流れについて」の詳細を入力"
                                                    resize="vertical"
                                                />
                                                {errors.process_flow_txt?.message && (
                                                    <FormError message={errors.process_flow_txt.message} />
                                                )}
                                            </div>
                                        )}
                                    />
                                )}

                                <Controller
                                    name="business_synergy"
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            id="business_synergy"
                                            disableBorder
                                            checked={field.value}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                        >
                                            {companyLabel}の想定する事業シナジー、事業展望、スキーム等について
                                        </FormCheckbox>
                                    )}
                                />
                                {watch('business_synergy') && (
                                    <Controller
                                        name="business_synergy_txt"
                                        control={control}
                                        render={({ field }) => (
                                            <div>
                                                <FormTextArea
                                                    {...field}
                                                    value={field.value ?? ''}
                                                    hasError={!!errors.business_synergy_txt?.message}
                                                    placeholder={`「${companyLabel}の想定する事業シナジー、事業展望、スキーム等について」の詳細を入力`}
                                                    resize="vertical"
                                                />
                                                {errors.business_synergy_txt?.message && (
                                                    <FormError message={errors.business_synergy_txt.message} />
                                                )}
                                            </div>
                                        )}
                                    />
                                )}
                                <Controller
                                    name="support_system"
                                    control={control}
                                    render={({ field }) => (
                                        <FormCheckbox
                                            id="support_system"
                                            disableBorder
                                            checked={field.value}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                        >
                                            支援体制、サポート内容について
                                        </FormCheckbox>
                                    )}
                                />
                                {watch('support_system') && (
                                    <Controller
                                        name="support_system_txt"
                                        control={control}
                                        render={({ field }) => (
                                            <div>
                                                <FormTextArea
                                                    {...field}
                                                    value={field.value ?? ''}
                                                    hasError={!!errors.support_system_txt?.message}
                                                    placeholder="「支援体制、サポート内容について」の詳細を入力"
                                                    resize="vertical"
                                                />
                                                {errors.support_system_txt?.message && (
                                                    <FormError message={errors.support_system_txt.message} />
                                                )}
                                            </div>
                                        )}
                                    />
                                )}
                            </FlexContainer>

                            <Description>その他の仲介会社への質問や要望があれば入力してください</Description>
                            <TemplateWrapper
                                disabled={loading}
                                aria-label="テンプレートを表示"
                                onClick={handleTemplateClick}
                            >
                                テンプレートから選ぶ
                                <img src={MailIcon} alt="テンプレート" />
                            </TemplateWrapper>
                            <Controller
                                name="question"
                                control={control}
                                render={({ field }) => (
                                    <div>
                                        <FormTextArea
                                            {...field}
                                            value={field.value ?? ''}
                                            hasError={!!errors.question?.message}
                                            placeholder="その他の質問や要望があれば入力してください"
                                            resize="vertical"
                                        />
                                        {errors.question?.message && <FormError message={errors.question.message} />}
                                    </div>
                                )}
                            />
                        </Section>

                        <SubTitleLabel>{companyLabel}の情報について教えてください</SubTitleLabel>
                        <Section>
                            <FlexContainer>
                                {notRegistrationComplete && (
                                    <FormCheckbox
                                        id="is_sole_proprietor"
                                        checked={!!watch('is_sole_proprietor')}
                                        onChange={() => setValue('is_sole_proprietor', !watch('is_sole_proprietor'))}
                                        disabled={!notRegistrationComplete}
                                    >
                                        <Description>個人事業主の方はこちらをチェックしてください</Description>
                                    </FormCheckbox>
                                )}
                            </FlexContainer>
                            <Description>
                                {watch('is_sole_proprietor')
                                    ? ' 屋号・ホームページURL・ロゴ画像はマッチングしたお相手にのみ開示されます'
                                    : '企業名・法人番号・企業画像・ホームページURLはマッチングしたお相手にのみ開示されます'}
                            </Description>

                            {!watch('is_sole_proprietor') && notRegistrationComplete && (
                                <SearchButton onClick={handleCompanySearchClick}>
                                    法人を検索する
                                    <img src={IconSearch} alt="search" />
                                </SearchButton>
                            )}
                            <FlexContainer>
                                <FormLabel
                                    title={watch('is_sole_proprietor') ? '個人事業主名（屋号）' : '企業名'}
                                    isRequired
                                />
                                {!watch('is_sole_proprietor') && notRegistrationComplete && (
                                    <Description>※法人を検索すると自動で入力されます</Description>
                                )}
                                <Controller
                                    name="name"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormInput
                                                {...field}
                                                hasError={!!errors.name?.message}
                                                disabled={!watch('is_sole_proprietor')}
                                            />
                                            {errors.name?.message && <FormError message={errors.name.message} />}
                                        </div>
                                    )}
                                />
                            </FlexContainer>
                            {!watch('is_sole_proprietor') && (
                                <FlexContainer>
                                    <FormLabel title="法人番号" isRequired />
                                    <Description>※法人を検索すると自動で入力されます</Description>
                                    <Controller
                                        name="company_code"
                                        control={control}
                                        render={({ field }) => (
                                            <div>
                                                <FormInput
                                                    {...field}
                                                    value={field.value ?? ''}
                                                    hasError={!!errors.company_code?.message}
                                                    disabled
                                                />
                                                {errors.company_code?.message && (
                                                    <FormError message={errors.company_code.message} />
                                                )}
                                            </div>
                                        )}
                                    />
                                </FlexContainer>
                            )}
                            <FlexContainer>
                                <FormLabel title={`${companyLabel}の従業員規模`} />
                                {employeeChoices && employeeChoices.length > 0 && (
                                    <Controller
                                        name="number_of_employees"
                                        control={control}
                                        render={({ field }) => (
                                            <div>
                                                <FormSelect
                                                    {...field}
                                                    value={field.value ?? ''}
                                                    options={employeeChoices.map(([value, label]) => ({
                                                        value: value,
                                                        label: label,
                                                    }))}
                                                />
                                                {errors.number_of_employees && (
                                                    <FormError message={errors.number_of_employees.message} />
                                                )}
                                            </div>
                                        )}
                                    />
                                )}
                            </FlexContainer>
                            <FlexContainer>
                                <FormLabel title={`${companyLabel}の売上高`} />
                                {revenueChoices && revenueChoices.length > 0 && (
                                    <Controller
                                        name="revenue_scale"
                                        control={control}
                                        render={({ field }) => (
                                            <div>
                                                <FormSelect
                                                    {...field}
                                                    value={field.value ?? ''}
                                                    options={revenueChoices.map(([value, label]) => ({
                                                        value: value,
                                                        label: label,
                                                    }))}
                                                />
                                                {errors.revenue_scale && (
                                                    <FormError message={errors.revenue_scale.message} />
                                                )}
                                            </div>
                                        )}
                                    />
                                )}
                            </FlexContainer>
                            <FlexContainer>
                                <FormLabel title={`${companyLabel}の事業内容`} />
                                <Controller
                                    name="business_description"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormTextArea
                                                {...field}
                                                value={field.value ?? ''}
                                                hasError={!!errors.business_description?.message}
                                                resize="vertical"
                                            />
                                            {errors.business_description?.message && (
                                                <FormError message={errors.business_description.message} />
                                            )}
                                        </div>
                                    )}
                                />
                            </FlexContainer>
                            <FlexContainer>
                                <FormLabel title="ホームページURL" />
                                <Controller
                                    name="hp_url"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormInput
                                                {...field}
                                                value={field.value ?? ''}
                                                hasError={!!errors.hp_url?.message}
                                            />
                                            {errors.hp_url?.message && <FormError message={errors.hp_url.message} />}
                                        </div>
                                    )}
                                />
                            </FlexContainer>

                            <FlexContainer>
                                <FormLabel title={watch('is_sole_proprietor') ? 'ロゴ画像' : '企業画像'} />
                                <ImageUpload
                                    imagePath={watch('icon_image') ?? ''}
                                    directory="buyer"
                                    fileName={'icon'}
                                    fileCategory="buyer"
                                    disabled={loading}
                                    width={180}
                                    height={180}
                                    onComplete={(fileUrl) => setValue('icon_image', fileUrl)}
                                />
                            </FlexContainer>
                        </Section>

                        <ButtonContainer>
                            <ActionButton
                                disabled={loading}
                                onClick={submit}
                                bgColor="main-color-blue"
                                style={{
                                    minWidth: '200px',
                                    maxWidth: '400px',
                                }}
                            >
                                保存する
                            </ActionButton>
                        </ButtonContainer>
                    </Wrapper>
                )}
                <Preview
                    buyer={{
                        ...watch(),
                        target_company_revenue_scale: getWindowText(
                            revenueScaleLowerChoices,
                            revenueScaleUpperChoices,
                            watch('target_company_revenue_scale_lower'),
                            watch('target_company_revenue_scale_upper'),
                        ),
                        target_company_profit_level: getWindowText(
                            profitLevelLowerChoices,
                            profitLevelUpperChoices,
                            watch('target_company_profit_level_lower'),
                            watch('target_company_profit_level_upper'),
                        ),
                        target_company_purchase_amount: getWindowText(
                            purchaseAmountLowerChoices,
                            purchaseAmountUpperChoices,
                            watch('target_company_purchase_amount_lower'),
                            watch('target_company_purchase_amount_upper'),
                        ),
                        target_company_employee_size: getWindowText(
                            employeeLowerChoices,
                            employeeUpperChoices,
                            watch('target_company_employee_size_lower'),
                            watch('target_company_employee_size_upper'),
                        ),
                        target_company_net_assets_level: getWindowText(
                            netAssetsLevelLowerChoices,
                            netAssetsLevelUpperChoices,
                            watch('target_company_net_assets_level_lower'),
                            watch('target_company_net_assets_level_upper'),
                        ),
                        label: user.buyer?.label || '(企業ラベル)',
                    }}
                    editing={editing}
                    areaChoices={areaChoices}
                    employeeChoices={employeeChoices}
                    revenueChoices={revenueChoices}
                    industryChoices={industryChoices}
                    loading={loading}
                    isPublish={!user.buyer?.unpublished}
                    setEditing={setEditing}
                    unPublish={unPublish}
                    reOpen={reOpen}
                />
            </Container>
        </>
    );
};
